import html2canvas from "html2canvas";
import { PDFDocument } from "pdf-lib";
import { InvoiceV2Request, InvoiceV2Item } from "../services/invoice-v2-api";

interface UserInfo {
  firstName: string;
  middleName?: string;
  lastName: string;
  homeAddress?: string;
}

/**
 * Creates a first page PDF from the custom HTML template populated with invoice data
 */
export const createInvoiceFormFirstPageFromTemplate = async (
  request: InvoiceV2Request,
  userInfo: UserInfo,
): Promise<PDFDocument> => {
  const container = document.createElement("div");
  container.style.position = "absolute";
  container.style.left = "-9999px";
  container.style.width = "1000px";
  container.style.backgroundColor = "#ffffff";

  const htmlContent = await loadAndPopulateTemplate(request, userInfo);
  container.innerHTML = htmlContent;
  document.body.appendChild(container);

  try {
    // Wait for content to render
    await new Promise((resolve) => setTimeout(resolve, 100));

    const canvas = await html2canvas(container, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: "#ffffff",
      allowTaint: true,
    });

    const pdfDoc = await PDFDocument.create();
    const imgData = canvas.toDataURL("image/png");
    const img = await pdfDoc.embedPng(imgData);

    const pageWidth = 841.89; // A4 landscape
    const pageHeight = 595.28;
    const imgWidth = pageWidth;
    const imgHeight = (canvas.height / canvas.width) * imgWidth;

    const page = pdfDoc.addPage([pageWidth, pageHeight]);
    page.drawImage(img, {
      x: 0,
      y: pageHeight - imgHeight,
      width: imgWidth,
      height: imgHeight,
    });

    return pdfDoc;
  } finally {
    document.body.removeChild(container);
  }
};

async function loadAndPopulateTemplate(
  request: InvoiceV2Request,
  userInfo: UserInfo,
): Promise<string> {
  const templatePath = "/templates/invoiceForm/InvoiceFormTemplate.html";
  const response = await fetch(templatePath);
  const html = await response.text();

  // Load and convert logo to base64
  const logoBase64 = await loadImageAsBase64(
    "/templates/invoiceForm/resources/excelfore-logo-dark.png",
  );

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  // Replace logo paths with base64 data
  const logoImages = doc.querySelectorAll(
    "img[src*='excelfore-logo-dark.png']",
  );
  logoImages.forEach((img) => {
    img.setAttribute("src", logoBase64);
  });

  // Format submission date
  const submissionDate = new Date(request.submissionDate);
  const formattedDate = submissionDate.toLocaleDateString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  // Format employee name
  const employeeName = `${userInfo.firstName}${
    userInfo.middleName ? " " + userInfo.middleName : ""
  } ${userInfo.lastName}`;

  // Populate header fields
  const submissionDateEl = doc.getElementById("submissionDate");
  if (submissionDateEl) submissionDateEl.textContent = formattedDate;

  const employeeNameEl = doc.getElementById("employeeName");
  if (employeeNameEl) employeeNameEl.textContent = employeeName;

  const employeeAddressEl = doc.getElementById("employeeAddress");
  if (employeeAddressEl)
    employeeAddressEl.textContent = userInfo.homeAddress || "";

  // Populate items table
  const itemsTableBody = doc.getElementById("itemsTableBody");
  if (itemsTableBody && request.items) {
    const itemsToAdd = request.items.slice(0, 20); // Support up to 20 items
    itemsToAdd.forEach((item) => {
      const row = createItemRow(item);
      itemsTableBody.appendChild(row);
    });

    // Add empty rows for visual consistency
    const emptyRowsCount = Math.max(0, 3 - itemsToAdd.length);
    for (let i = 0; i < emptyRowsCount; i++) {
      const emptyRow = createEmptyRow();
      itemsTableBody.appendChild(emptyRow);
    }
  }

  // Populate total amount
  const totalAmountEl = doc.getElementById("totalAmount");
  if (totalAmountEl) {
    const totalFormatted = request.totalAmount.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    totalAmountEl.textContent = totalFormatted;
  }

  // Populate payment instructions if available
  if (request.paymentInstruction) {
    const paymentSection = doc.getElementById("paymentSection");
    if (paymentSection) {
      paymentSection.style.display = "block";

      const swiftCodeEl = doc.getElementById("swiftCode");
      if (swiftCodeEl)
        swiftCodeEl.textContent = request.paymentInstruction.swiftCode || "";

      const accountNameEl = doc.getElementById("accountName");
      if (accountNameEl)
        accountNameEl.textContent =
          request.paymentInstruction.accountName || "";

      const accountCodeEl = doc.getElementById("accountCode");
      if (accountCodeEl)
        accountCodeEl.textContent =
          request.paymentInstruction.accountCode || "";

      const bankNameEl = doc.getElementById("bankName");
      if (bankNameEl)
        bankNameEl.textContent = request.paymentInstruction.bankName || "";

      const bankBranchEl = doc.getElementById("bankBranch");
      if (bankBranchEl)
        bankBranchEl.textContent = request.paymentInstruction.bankBranch || "";

      const bankAddressEl = doc.getElementById("bankAddress");
      if (bankAddressEl)
        bankAddressEl.textContent =
          request.paymentInstruction.bankAddress || "";

      const remarksEl = doc.getElementById("remarks");
      if (remarksEl)
        remarksEl.textContent = request.paymentInstruction.remarks || "";
    }
  }

  return doc.documentElement.outerHTML;
}

function createItemRow(item: InvoiceV2Item): HTMLTableRowElement {
  const row = document.createElement("tr");

  const descriptionCell = document.createElement("td");
  descriptionCell.className = "description-col";
  descriptionCell.textContent = item.description || "";

  const quantityCell = document.createElement("td");
  quantityCell.className = "quantity-col";
  quantityCell.textContent = item.quantity?.toString() || "";

  const priceCell = document.createElement("td");
  priceCell.className = "price-col";
  priceCell.textContent = item.pricePerUnit
    ? item.pricePerUnit.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : "";

  const currencyCell = document.createElement("td");
  currencyCell.className = "currency-col";
  currencyCell.textContent = item.currency || "";

  const amountCell = document.createElement("td");
  amountCell.className = "amount-col";
  amountCell.textContent = item.amount
    ? item.amount.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : "";

  row.appendChild(descriptionCell);
  row.appendChild(quantityCell);
  row.appendChild(priceCell);
  row.appendChild(currencyCell);
  row.appendChild(amountCell);

  return row;
}

function createEmptyRow(): HTMLTableRowElement {
  const row = document.createElement("tr");

  for (let i = 0; i < 5; i++) {
    const cell = document.createElement("td");
    cell.textContent = "";
    row.appendChild(cell);
  }

  return row;
}

async function loadImageAsBase64(imagePath: string): Promise<string> {
  try {
    const response = await fetch(imagePath);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error(`Failed to load image from ${imagePath}:`, error);
    return ""; // Return empty string if image fails to load
  }
}
